/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaExclamationTriangle } from 'react-icons/fa';

// =============================================================================
// Avisos NÃO-BLOQUEANTES de saldo (handoff 0099 §3.6) — faixa âmbar
// dispensável no topo do detalhe. Substituiu o modal em 2026-09-10 (decisão
// D4 da refatoração visual): mesma substância — quando isto aparece, a
// operação JÁ aconteceu; é relato do efeito, não guard. Por isso a única ação
// é dispensar (×), nunca "confirmar mesmo assim".
//
// Duas naturezas de aviso, com redações distintas:
//   · `stock_warnings`          → o saldo FÍSICO da origem ficou negativo;
//   · `transfer_stock_warnings` → físico − reservado por OUTRAS transferências
//     ficou negativo (reserva sem lastro).
//
// O fundo é OPACO de propósito: a faixa senta no gradiente escuro do shell, e
// fundo translúcido fora dos cartões some (regra do handoff — já quebrou 2×).
// =============================================================================

const Banner = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 12px;
   margin-bottom: 16px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid #ecd9ab;
   background: #fdf6e3;
`;

const WarnIcon = styled.span`
   flex-shrink: 0;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 30px;
   height: 30px;
   border-radius: 8px;
   background: rgba(217, 119, 6, 0.14);
   color: #8a5a00;
`;

const Content = styled.div`
   flex: 1;
   min-width: 0;

   .ttl {
      font-size: 12.5px;
      font-weight: 800;
      color: #8a5a00;
   }
   ul {
      margin: 5px 0 0;
      padding-left: 16px;
      display: grid;
      gap: 3px;
   }
   li {
      font-size: 12.5px;
      line-height: 1.5;
      color: #6b4600;
   }
`;

const DismissBtn = styled.button`
   appearance: none;
   flex-shrink: 0;
   border: 0;
   width: 26px;
   height: 26px;
   border-radius: 7px;
   background: transparent;
   color: #8a5a00;
   font-size: 15px;
   line-height: 1;
   cursor: pointer;

   /* G.4.1: hover precisa declarar background, senão herda o azul do legado. */
   &:hover:not(:disabled) {
      background: rgba(217, 119, 6, 0.14);
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

function label(warning) {
   return warning.prod_name ?? warning.prod_sku ?? `Produto ${warning.prod_id}`;
}

export default function StockWarningsBanner({
   subtitle = null,
   stockWarnings = [],
   transferStockWarnings = [],
   onDismiss,
}) {
   if (stockWarnings.length === 0 && transferStockWarnings.length === 0) {
      return null;
   }

   return (
      <Banner role="status">
         <WarnIcon aria-hidden="true">
            <FaExclamationTriangle size={13} />
         </WarnIcon>

         <Content>
            <div className="ttl">
               Aviso de saldo — a operação já foi registrada
               {subtitle ? ` (${subtitle})` : ''}
            </div>
            <ul>
               {stockWarnings.map((warning) => (
                  <li key={`stock-${warning.pdv_id}-${warning.prod_id}`}>
                     <strong>{label(warning)}</strong> ficou com saldo físico
                     negativo na origem ({warning.resulting_qtd}). Confira o
                     inventário do produto.
                  </li>
               ))}
               {transferStockWarnings.map((warning) => (
                  <li key={`transfer-${warning.pdv_id}-${warning.prod_id}`}>
                     <strong>{label(warning)}</strong>: reserva sem lastro na
                     origem — físico {warning.physical_qtd}, reservado por
                     outras transferências {warning.reserved_qtd}, disponível{' '}
                     {warning.available_qtd}.
                  </li>
               ))}
            </ul>
         </Content>

         <DismissBtn
            type="button"
            onClick={onDismiss}
            aria-label="Dispensar aviso"
         >
            ×
         </DismissBtn>
      </Banner>
   );
}

const warnShape = PropTypes.shape({
   pdv_id: PropTypes.number,
   prod_id: PropTypes.number,
   prod_sku: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   prod_name: PropTypes.string,
});

StockWarningsBanner.propTypes = {
   subtitle: PropTypes.node,
   stockWarnings: PropTypes.arrayOf(warnShape),
   transferStockWarnings: PropTypes.arrayOf(warnShape),
   onDismiss: PropTypes.func.isRequired,
};
